import React, { useState } from "react";
import EGAccordion from "../../../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import { Box, Dialog, DialogContent, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditMeasuresForm } from "./EditMeasuresForm";
import {
  deleteCompensations,
  deleteMeasures,
  deleteSanctions,
} from "../../../../../../../apis";
import { useSnackbar } from "notistack";
import { EditSanctionsCompensationsForm } from "./EditSanctionsCompensationsForm";
import { Measures } from "./Measures";
import { SanctionsAndCompensations } from "./SanctionsAndCompensations";
import { useReportId } from "../../../../../../../context/report/ReportContext";

export const TakenActions = ({
  measures,
  getAllMeasures,
  measureCatalogOptions,
  sanctions,
  getAllSanctions,
  compensations,
  getAllCompensations,
  reportRender,
  openModalConfirm,
  closeModalConfirm,
  setIsLoading,
}) => {
  // Custom Hooks
  const theme = useTheme();
  const [t] = useTranslation("report");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const id = useReportId();

  // Initial States
  const [openEditActions, setOpenEditActions] = useState(false);
  const [dataToEdit, setDataToEdit] = useState({
    type: null,
    content: null,
  });
  

  // Open/Close edit Dialog
  const hdlOpenDialog = (type, content) => {
    setOpenEditActions(true);
    setDataToEdit({ type, content });
  };
  const hdlCloseDialog = () => {
    setOpenEditActions(false);
    setDataToEdit({ type: null, content: null });
  };

  // Obtaining label by saved value
  const getLabelByValue = (options, value) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : null;
  };
  // Delete Actions
  const handleDelete = async (
    item,
    deleteFunction,
    successMessage,
    refreshFunction
  ) => {
    setIsLoading(true);
    const deleting = enqueueSnackbar(`${t("Resolution.deleting")}...`, {
      variant: "info",
      persist: true,
    });
    try {
      await deleteFunction(id, item.id).then((resp) => {
        if (resp.status === 200) {
          closeSnackbar(deleting);
          setIsLoading(false);
          enqueueSnackbar(t(successMessage), { variant: "success" });
          refreshFunction();
          reportRender();
          closeModalConfirm();
        } else {
          closeSnackbar(deleting);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error(error);
      closeSnackbar(deleting);
      setIsLoading(false);
    }
  };
  // Delete functions
  const hdlDeleteMeasure = (measure) =>
    handleDelete(
      measure,
      deleteMeasures,
      "Resolution.measure_deleted_successfully",
      getAllMeasures
    );
  const hdlDeleteSanction = (sanction) =>
    handleDelete(
      sanction,
      deleteSanctions,
      "Resolution.sanction_deleted_successfully",
      getAllSanctions
    );
  const hdlDeleteCompensation = (benefit) =>
    handleDelete(
      benefit,
      deleteCompensations,
      "Resolution.compensation_deleted_successfully",
      getAllCompensations
    );

  // Form corresponding to data to be edited
  const renderForm = () => {
    const { type, content } = dataToEdit;

    if (type === "measures") {
      return (
        <EditMeasuresForm
          content={content}
          hdlCloseDialog={hdlCloseDialog}
          getAllMeasures={getAllMeasures}
          reportRender={reportRender}
          measureCatalogOptions={measureCatalogOptions}
        />
      );
    } else if (type === "sanctions") {
      return (
        <EditSanctionsCompensationsForm
          content={content}
          hdlCloseDialog={hdlCloseDialog}
          reloadData={getAllSanctions}
          reportRender={reportRender}
          type={type}
        />
      );
    } else if (type === "benefits") {
      return (
        <EditSanctionsCompensationsForm
          content={content}
          hdlCloseDialog={hdlCloseDialog}
          reloadData={getAllSanctions}
          reportRender={reportRender}
          type={type}
        />
      );
    }
  };
  // Accordion contents
  const accordionPanels = [
    {
      id: 1,
      title: t("Resolution.corrective_preventive_measures"),
      titleBackgrundColor: theme.palette.aquaSky.main,
      titleColor: theme.palette.background.paper,
      contentBorderColor: theme.palette.aquaSky.main,
      content:
        measures.length > 0 ? (
          <Measures
            measures={measures}
            hdlOpenDialog={hdlOpenDialog}
            openModalConfirm={openModalConfirm}
            hdlDeleteMeasure={hdlDeleteMeasure}
            closeModalConfirm={closeModalConfirm}
            getLabelByValue={getLabelByValue}
            measureCatalogOptions={measureCatalogOptions}
          />
        ) : null,
    },
    {
      id: 2,
      title: t("Resolution.sanctions"),
      titleBackgrundColor: theme.palette.orange.main,
      titleColor: theme.palette.background.paper,
      contentBorderColor: theme.palette.orange.main,
      content:
        sanctions.length > 0 ? (
          <SanctionsAndCompensations
            actions={sanctions}
            actionsType={"sanctions"}
            deleteFunction={hdlDeleteSanction}
            openModalConfirm={openModalConfirm}
            closeModalConfirm={closeModalConfirm}
            hdlOpenDialog={hdlOpenDialog}
          />
        ) : null,
    },
    {
      id: 3,
      title: t("Resolution.remunerations_compesation"),
      titleBackgrundColor: theme.palette.vividViolet.main,
      titleColor: theme.palette.background.paper,
      contentBorderColor: theme.palette.vividViolet.main,
      content:
        compensations.length > 0 ? (
          <SanctionsAndCompensations
            actions={compensations}
            actionsType={"compensations"}
            deleteFunction={hdlDeleteCompensation}
            openModalConfirm={openModalConfirm}
            closeModalConfirm={closeModalConfirm}
            hdlOpenDialog={hdlOpenDialog}
          />
        ) : null,
    },
  ];
  // Filter accordions with content
  const accordionsActions = accordionPanels.filter(
    (elements) => elements.content !== null
  );

  return (
    <Box mt={1}>
      <Dialog
        open={openEditActions}
        PaperProps={{ component: "form" }}
        fullWidth
      >
        <DialogContent>{renderForm()}</DialogContent>
      </Dialog>
      <EGAccordion panels={accordionsActions} defaultExpanded={1} />
    </Box>
  );
};
